import React, { createContext, useState, useContext, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles, createTheme , ThemeProvider as MUIThemeProvider } from "@material-ui/core/styles";
import { CssBaseline } from "@material-ui/core";

const ThemeContext = createContext();

const useGlobalStyles = makeStyles(theme => ({
  '@global': {
    '.dark-mode .react-datepicker': {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      border: `1px solid ${theme.palette.divider}`,
    },
    '.dark-mode .react-datepicker__triangle path': {
      stroke: `${theme.palette.background.paper} !important`,
    },
    '.dark-mode .react-datepicker__triangle path:last-of-type': {
      stroke: `${theme.palette.divider} !important`,
    },
    '.dark-mode .react-datepicker__header': {
      backgroundColor: theme.palette.background.default,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '.dark-mode .react-datepicker__current-month': {
      color: theme.palette.text.primary,
    },
    '.dark-mode .react-datepicker__day-name': {
      color: theme.palette.text.secondary,
    },
    '.dark-mode .react-datepicker__day': {
      color: theme.palette.text.primary,
    },
    '.dark-mode .react-datepicker__day:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '.dark-mode .react-datepicker__day--selected': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      fontWeight: 'bold',
    },
    '.dark-mode .react-datepicker__day--selected:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    '.dark-mode .react-datepicker__day--today': {
      fontWeight: 'bold',
      border: `1px solid ${theme.palette.text.secondary}`,
      borderRadius: '50%',
    },
    '.dark-mode .react-datepicker__day--outside-month': {
      color: theme.palette.text.disabled,
    },
    '.dark-mode .react-datepicker__navigation-icon::before': {
      borderColor: theme.palette.text.primary,
    },
  },
}));

const GlobalDatePickerStyles = () => {
  useGlobalStyles();
  return null;
};

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(() => {
    // Leer el valor guardado en localStorage si existe
    const savedTheme = localStorage.getItem("darkMode");
    return savedTheme ? JSON.parse(savedTheme) : false;  // default false
  });

  useEffect(() => {
    const body = document.querySelector("body");
    if (darkMode) {
      body.classList.add("dark-mode");
    } else {
      body.classList.remove("dark-mode");
    }
  }, [darkMode]);
  
  const toggleTheme = () => {
    setDarkMode(prevMode => {
      const newMode = !prevMode;
      localStorage.setItem("darkMode", JSON.stringify(newMode));
      return newMode;
    });
  };

  const theme = useMemo(
    () =>
      createTheme ({
        palette: {
          type: darkMode ? "dark" : "light",
          primary: { main: "#2576d2" },
          secondary: { main: "#d81b60" },
        },
        scrollbarStyles: {
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            boxShadow: "inset 0 0 6px rgba(0, 0, 0, 0.3)",
            backgroundColor: "#e8e8e8",
          },
        },
      }),
    [darkMode]
  );

  const contextValue = useMemo(() => ({ darkMode, toggleTheme }), [darkMode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalDatePickerStyles />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useThemeContext = () => useContext(ThemeContext);
